/**
 * UpdateNoticeModal - 업데이트/공지 팝업
 * 오너 로그인 시 HQ가 등록한 공지(show_popup)를 표시.
 * 내용은 HQ 공지 관리(/api/v1/owner/notices/active)에서 불러옴 — 코드 하드코딩 X.
 * '확인했습니다'/'오늘 하루 보지 않기'는 공지 id+updated_at 기준으로 기억 → 수정하면 다시 뜸.
 */
import React, { useState, useEffect } from 'react';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';

interface Notice {
  id: string;
  title: string;
  body: string;
  version: string | null;
  show_popup: boolean;
  show_dashboard: boolean;
  updated_at: string | null;
}

const getToken = () =>
  localStorage.getItem('auth_access_token') ||
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token');

const seenKey = (n: Notice) => `owner_notice_seen_${n.id}_${n.updated_at || ''}`;
const todayKey = (n: Notice) => `owner_notice_today_${n.id}_${n.updated_at || ''}`;
const getTodayString = () => {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
};

const UpdateNoticeModal: React.FC = () => {
  const [notice, setNotice] = useState<Notice | null>(null);
  const [dontShowToday, setDontShowToday] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        if (!token) return;
        const res = await fetch(`${API_BASE_URL}/api/v1/owner/notices/active`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const today = getTodayString();
        const popups: Notice[] = (data.notices || []).filter((n: Notice) => n.show_popup);
        // 아직 '확인'하지 않았고 '오늘 보지 않기'도 아닌 첫 공지
        const target = popups.find(
          (n) =>
            !localStorage.getItem(seenKey(n)) &&
            localStorage.getItem(todayKey(n)) !== today,
        );
        if (target && !cancelled) {
          setTimeout(() => {
            if (!cancelled) setNotice(target);
          }, 500);
        }
      } catch {
        /* 공지 실패해도 앱 사용에 영향 없음 */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!notice) return null;

  const handleClose = () => {
    if (dontShowToday) localStorage.setItem(todayKey(notice), getTodayString());
    setNotice(null);
  };
  const handleConfirm = () => {
    localStorage.setItem(seenKey(notice), '1');
    setNotice(null);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0">
              <SparklesIcon className="h-6 w-6 flex-shrink-0" />
              <h2 className="text-xl font-bold truncate">{notice.title}</h2>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:text-gray-200 transition-colors flex-shrink-0"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          {notice.version && <p className="text-blue-100 text-sm mt-1">{notice.version}</p>}
        </div>

        {/* 내용 */}
        <div className="px-6 py-4 overflow-y-auto max-h-[50vh]">
          <p className="text-gray-700 text-sm whitespace-pre-line leading-relaxed">
            {notice.body}
          </p>
        </div>

        {/* 푸터 */}
        <div className="px-6 py-4 bg-gray-50 border-t space-y-3">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={dontShowToday}
              onChange={(e) => setDontShowToday(e.target.checked)}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm text-gray-600">오늘 하루 다시 보지 않기</span>
          </label>
          <div className="flex space-x-2">
            <button
              onClick={handleClose}
              className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 transition-colors"
            >
              닫기
            </button>
            <button
              onClick={handleConfirm}
              className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              확인했습니다
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateNoticeModal;
